import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminDocumentUpload from '@/components/admin/AdminDocumentUpload';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Search, FileText, Eye, Download, CheckCircle, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Document {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  tax_year: number;
  extraction_status: string;
  extracted_data: any;
  uploaded_at: string;
  admin_approved_by: string | null;
  admin_approved_at: string | null;
  is_archived: boolean;
  clients: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface Client {
  id: string;
  full_name: string;
}

export default function AdminDocuments() {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewFileUrl, setViewFileUrl] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const { data, error } = await supabase
          .from('client_documents')
          .select('*, clients!inner(id, full_name, email)')
          .order('uploaded_at', { ascending: false });

        if (error) throw error;
        setDocuments(data || []);
        setFilteredDocuments(data || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchClients = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, full_name')
          .order('full_name');

        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchDocuments();
    fetchClients();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-documents')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_documents' }, () => {
        fetchDocuments();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let filtered = documents;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.file_name.toLowerCase().includes(query) ||
          doc.clients.full_name.toLowerCase().includes(query) ||
          doc.clients.email.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.extraction_status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.file_type === typeFilter);
    }

    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.clients.id === clientFilter);
    }

    // Archive filter
    if (!showArchived) {
      filtered = filtered.filter((doc) => !doc.is_archived);
    }

    setFilteredDocuments(filtered);
  }, [searchQuery, statusFilter, typeFilter, clientFilter, showArchived, documents]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      completed: "default",
      failed: "destructive",
      needs_review: "outline",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleApproveAndArchive = async (docId: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('client_documents')
        .update({
          extraction_status: 'completed',
          admin_approved_by: userData.user.id,
          admin_approved_at: new Date().toISOString(),
          is_archived: true,
        })
        .eq('id', docId);

      if (error) throw error;

      toast({
        title: 'Document Approved & Archived',
        description: 'The document has been approved and will appear in the client portal.',
      });

      setSelectedDoc(null);
    } catch (error) {
      console.error('Error approving document:', error);
      toast({
        title: 'Action Failed',
        description: 'There was an error processing the document.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (doc: Document) => {
    setIsDeleting(true);
    try {
      // 1. Delete from storage
      const { error: storageError } = await supabase.storage
        .from('tax-documents')
        .remove([doc.file_path]);
      
      if (storageError) throw storageError;

      // 2. Delete from database
      const { error: dbError } = await supabase
        .from('client_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      toast({
        title: 'Document deleted',
        description: 'The document has been permanently deleted.',
      });

      setDeleteDoc(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Deletion failed',
        description: 'There was an error deleting the document.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDocument = async (doc: Document) => {
    setSelectedDoc(doc);
    setIsLoadingFile(true);
    setViewFileUrl(null);

    try {
      // Generate signed URL valid for 1 hour
      const { data, error } = await supabase.storage
        .from('tax-documents')
        .createSignedUrl(doc.file_path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        setViewFileUrl(data.signedUrl);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      toast({
        title: 'Failed to load document',
        description: 'There was an error loading the document preview.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleDownloadDocument = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('tax-documents')
        .download(doc.file_path);

      if (error) throw error;

      // Create download link
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Download started',
        description: 'Document is being downloaded.',
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Download failed',
        description: 'There was an error downloading the document.',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Document Management</h1>
          <p className="text-muted-foreground">
            View, manage, and upload documents for all clients
          </p>
        </div>

        <Tabs defaultValue="view" className="w-full">
          <TabsList>
            <TabsTrigger value="view">View Documents</TabsTrigger>
            <TabsTrigger value="upload">Upload for Client</TabsTrigger>
          </TabsList>

          <TabsContent value="view" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>All Documents</CardTitle>
                <CardDescription>
                  Review uploaded documents and extracted data
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="space-y-4 mb-6">
                  <div className="grid gap-4 md:grid-cols-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by filename or client..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="needs_review">Needs Review</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="w2">W-2</SelectItem>
                        <SelectItem value="1099-misc">1099-MISC</SelectItem>
                        <SelectItem value="1099-int">1099-INT</SelectItem>
                        <SelectItem value="1099-nec">1099-NEC</SelectItem>
                        <SelectItem value="receipt">Receipt</SelectItem>
                        <SelectItem value="social-security-card">Social Security Card</SelectItem>
                        <SelectItem value="drivers-license">Drivers License</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={clientFilter} onValueChange={setClientFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="show-archived"
                      checked={showArchived}
                      onCheckedChange={setShowArchived}
                    />
                    <Label htmlFor="show-archived" className="cursor-pointer">
                      Show archived documents
                    </Label>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading documents...</p>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                        ? 'No documents found'
                        : 'No documents yet'}
                    </h3>
                    <p className="text-muted-foreground">
                      {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Documents will appear here when clients upload them'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>File Name</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Size</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Archived</TableHead>
                            <TableHead>Uploaded</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredDocuments.map((doc) => (
                            <TableRow key={doc.id}>
                              <TableCell className="font-medium max-w-xs truncate">
                                {doc.file_name}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{doc.clients.full_name}</p>
                                  <p className="text-xs text-muted-foreground">{doc.clients.email}</p>
                                </div>
                              </TableCell>
                              <TableCell className="uppercase text-xs">{doc.file_type}</TableCell>
                              <TableCell className="text-xs">{formatFileSize(doc.file_size)}</TableCell>
                              <TableCell>{getStatusBadge(doc.extraction_status)}</TableCell>
                              <TableCell>
                                {doc.is_archived ? (
                                  <Badge variant="secondary" className="text-xs">✓ Archived</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs">
                                {format(new Date(doc.uploaded_at), 'PP')}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewDocument(doc)}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteDoc(doc)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="mt-4 text-sm text-muted-foreground">
                      Showing {filteredDocuments.length} of {documents.length} document(s)
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upload">
            <AdminDocumentUpload />
          </TabsContent>
        </Tabs>
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => {
        setSelectedDoc(null);
        setViewFileUrl(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
            <DialogDescription>
              Review document information and extracted data
            </DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-6">
              {/* Document Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Document Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-muted-foreground">File Name</p>
                      <p>{selectedDoc.file_name}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Client</p>
                      <p>{selectedDoc.clients.full_name}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Document Type</p>
                      <p className="uppercase">{selectedDoc.file_type}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Tax Year</p>
                      <p>{selectedDoc.tax_year}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Status</p>
                      {getStatusBadge(selectedDoc.extraction_status)}
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Uploaded</p>
                      <p>{format(new Date(selectedDoc.uploaded_at), 'PPp')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* File Preview */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Document Preview</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectedDoc && handleDownloadDocument(selectedDoc)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </CardHeader>
                <CardContent>
                  {isLoadingFile ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                  ) : viewFileUrl ? (
                    <div className="border rounded-lg overflow-hidden">
                      {selectedDoc.file_name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                        // Image preview
                        <img 
                          src={viewFileUrl} 
                          alt={selectedDoc.file_name}
                          className="w-full h-auto max-h-[500px] object-contain bg-muted"
                        />
                      ) : selectedDoc.file_name.match(/\.pdf$/i) ? (
                        // PDF preview
                        <iframe
                          src={viewFileUrl}
                          className="w-full h-[500px]"
                          title={selectedDoc.file_name}
                        />
                      ) : (
                        // Other file types
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                          <FileText className="h-16 w-16 text-muted-foreground" />
                          <div className="text-center">
                            <p className="font-medium">Preview not available</p>
                            <p className="text-sm text-muted-foreground">
                              Click download to view this file
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      Failed to load document preview
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Extracted Data */}
              {selectedDoc.extracted_data && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Extracted Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-96">
                      {JSON.stringify(selectedDoc.extracted_data, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2">
                {!selectedDoc.admin_approved_by && !selectedDoc.is_archived && (
                  <Button onClick={() => handleApproveAndArchive(selectedDoc.id)}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve & Archive
                  </Button>
                )}
                {selectedDoc.admin_approved_by && selectedDoc.is_archived && (
                  <Badge variant="default" className="px-4 py-2">
                    ✓ Approved & Archived
                  </Badge>
                )}
                {selectedDoc.admin_approved_by && !selectedDoc.is_archived && (
                  <Badge variant="secondary" className="px-4 py-2">
                    Approved (Not Archived)
                  </Badge>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDoc?.file_name}" uploaded by {deleteDoc?.clients.full_name}?
              This action cannot be undone and will permanently remove the file from storage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDoc && handleDelete(deleteDoc)}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
